export const CMS_API_BASE = 'https://api.dev.auxo.dk/public/v2/content'

export async function fetchCmsCollection(collectionName, params = {}) {
  const url = new URL(`${CMS_API_BASE}/${collectionName}`)

  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null && item !== '') {
          url.searchParams.append(key, String(item))
        }
      })
      return
    }

    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value))
    }
  })

  const response = await fetch(url.toString())

  if (!response.ok) {
    throw new Error(`Kunne ikke hente CMS-kollektionen ${collectionName}`)
  }

  return response.json()
}

export function stripHtml(value = '') {
  return value
    .replace(/<br\s*\/?>(\s*)/gi, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export function excerptFromHtml(html = '', maxLength = 180) {
  const text = stripHtml(html)
  if (!text) return ''
  if (text.length <= maxLength) return text
  return `${text.slice(0, maxLength - 1).trimEnd()}…`
}

export function optimizeAssetUrl(url, width = 1200, quality = 80) {
  if (!url) return ''
  if (!url.startsWith('https://assets.auxodev.dk/') || url.includes('/cdn-cgi/image/')) {
    return url
  }

  return url.replace(
    'https://assets.auxodev.dk/',
    `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=${quality},format=auto/`
  )
}

export function buildResponsiveImageProps(url, widths = [480, 800, 1200, 1600]) {
  const src = optimizeAssetUrl(url, widths[widths.length - 1] ?? 1200)
  const srcSet = widths
    .map((width) => `${optimizeAssetUrl(url, width)} ${width}w`)
    .join(', ')

  return {
    src,
    srcSet,
    sizes: '(min-width: 1280px) 1200px, (min-width: 768px) 50vw, 100vw',
  }
}

export function findCmsPageBySlug(pages = [], slug) {
  return pages.find((page) => {
    const pageSlug = page?.slug?.da || page?.slug
    if (pageSlug === slug) return true
    if ((slug === 'tens' || slug === 'tns') && (pageSlug === 'tens' || pageSlug === 'tns')) return true
    return false
  })
}

export function findCmsPagesByCategory(pages = [], category) {
  return pages.filter((page) => page?.category === category)
}

export function getCmsPageText(page) {
  if (!page) return ''
  return page.content || page.summary || ''
}

export function getCmsPageTitle(page) {
  if (!page) return ''
  return page.title || ''
}

export function parseCmsAccordions(rawAccordions, fallbackList = []) {
  if (!rawAccordions) return fallbackList
  if (Array.isArray(rawAccordions)) {
    return rawAccordions.length > 0 ? rawAccordions : fallbackList
  }
  if (typeof rawAccordions !== 'string') return fallbackList
  const trimmed = rawAccordions.trim()
  if (!trimmed) return fallbackList

  // 1. Try JSON
  if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || (trimmed.startsWith('{') && trimmed.endsWith('}'))) {
    try {
      const parsed = JSON.parse(trimmed)
      const list = Array.isArray(parsed) ? parsed : (parsed.items || parsed.accordions || parsed.faqs)
      if (Array.isArray(list) && list.length > 0) {
        return list.map((item, idx) => ({
          id: item.id || idx,
          title: item.title || item.question || `Punkt ${idx + 1}`,
          description: item.description || item.content || item.answer || ''
        }))
      }
    } catch {
      // Continue to HTML parsing
    }
  }

  // 2. Try HTML parsing in browser environment
  if (typeof window !== 'undefined' && window.DOMParser) {
    try {
      const parser = new DOMParser()
      const doc = parser.parseFromString(trimmed, 'text/html')

      // A. Check for <details>
      const details = Array.from(doc.querySelectorAll('details'))
      if (details.length > 0) {
        const items = details.map((el, idx) => {
          const summary = el.querySelector('summary')
          const title = summary ? summary.textContent.trim() : `Punkt ${idx + 1}`
          if (summary) summary.remove()
          return {
            id: idx,
            title,
            description: el.innerHTML.trim()
          }
        }).filter(item => item.title || item.description)

        if (items.length > 0) return items
      }

      // B. Check for headings (h2, h3, h4)
      const headings = Array.from(doc.querySelectorAll('h2, h3, h4'))
      if (headings.length > 0) {
        const items = []
        headings.forEach((heading, idx) => {
          const title = heading.textContent.trim()
          let contentHtml = ''
          let nextEl = heading.nextElementSibling
          while (nextEl && !['H2', 'H3', 'H4'].includes(nextEl.tagName)) {
            contentHtml += nextEl.outerHTML
            nextEl = nextEl.nextElementSibling
          }
          items.push({
            id: idx,
            title,
            description: contentHtml.trim()
          })
        })
        if (items.length > 0) return items
      }
    } catch {
      // Fallback
    }
  }

  return fallbackList
}
